/**
 * useVacationWeekData.ts
 * Custom hook: fetches per-week working-day counts for a vacation/sick range.
 *
 * Follow-up 1.6 — Per-week working-days call for Vacation percent.
 *
 * Replaces the previous approximation `totalWorkingDays ÷ weeks × 20`.
 * For each BST week overlapping [startDate, endDate], this hook calls
 * GET /api/schedule/working-days once and caches the result in a ref so that
 * tweaking the date range does not repeat completed calls for unchanged weeks.
 *
 * Cache key: "weekStart|fetchStart|fetchEnd" — partial-first and partial-last
 * weeks cache separately from full-week calls, so adjusting the end date only
 * re-fetches the affected week.
 *
 * Returns:
 *   perWeekData    Map<weekStart, WeekWorkingData>  — populated as fetches complete.
 *   loading        true while at least one fetch is in-flight.
 *   allHolidays    Deduplicated union of HolidayEntry across all weeks (for display).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchWorkingDays, type HolidayEntry } from '../../../services/scheduleEmployeeSchedule';

// ---------------------------------------------------------------------------
// Helpers (pure, self-contained)
// ---------------------------------------------------------------------------

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

function weekStartFor(dateStr: string): string {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return addDays(dateStr, dow === 6 ? 0 : -(dow + 1));
}

function clamp(date: string, lo: string, hi: string): string {
  return date < lo ? lo : date > hi ? hi : date;
}

function weekStartsInRange(start: string, end: string): string[] {
  const starts: string[] = [];
  let cur = weekStartFor(start);
  while (cur <= end) { starts.push(cur); cur = addDays(cur, 7); }
  return starts;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WeekWorkingData {
  workingDays: number;
  /** Full HolidayEntry objects for display (with names). */
  holidays: HolidayEntry[];
}

export interface UseVacationWeekDataResult {
  /** Map from weekStart (Saturday ISO) to working-day data for that week. */
  perWeekData: ReadonlyMap<string, WeekWorkingData>;
  loading: boolean;
  /** Deduplicated union of all holidays across all weeks, sorted by date. */
  allHolidays: HolidayEntry[];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches per-week working-day data for [startDate, endDate].
 * Only runs fetches when `active` is true (Vacation/Sick types).
 * Cache is cleared when `active` flips false→true (dialog re-open).
 */
export function useVacationWeekData(
  startDate: string,
  endDate: string,
  active: boolean,
  region = 'NB',
): UseVacationWeekDataResult {
  const [perWeekData, setPerWeekData] = useState<Map<string, WeekWorkingData>>(new Map());
  const [loading, setLoading] = useState(false);
  const cache = useRef<Map<string, WeekWorkingData>>(new Map());

  const fetchWeeks = useCallback(async (start: string, end: string) => {
    if (!start || !end || start > end) return;
    const weekStarts = weekStartsInRange(start, end);
    setLoading(true);

    const nextMap = new Map<string, WeekWorkingData>();
    await Promise.all(weekStarts.map(async (ws) => {
      const wFri = addDays(ws, 6);
      const fetchStart = clamp(start, ws, wFri);
      const fetchEnd = clamp(end, ws, wFri);
      const key = `${ws}|${fetchStart}|${fetchEnd}`;

      let entry = cache.current.get(key);
      if (!entry) {
        try {
          const result = await fetchWorkingDays(fetchStart, fetchEnd, region);
          entry = { workingDays: result.workingDayCount, holidays: result.holidaysInRange };
          cache.current.set(key, entry);
        } catch {
          entry = { workingDays: 5, holidays: [] };
        }
      }
      nextMap.set(ws, entry);
    }));

    setPerWeekData(nextMap);
    setLoading(false);
  }, [region]);

  // Clear cache when dialog deactivates (closed / type changed away from vacation).
  useEffect(() => {
    if (!active) {
      cache.current.clear();
      setPerWeekData(new Map());
      setLoading(false);
    }
  }, [active]);

  useEffect(() => {
    if (active) void fetchWeeks(startDate, endDate);
  }, [active, startDate, endDate, fetchWeeks]);

  // Build a deduplicated holiday list across all weeks for the date-range display.
  const holidayMap = new Map<string, HolidayEntry>();
  for (const { holidays } of perWeekData.values()) {
    for (const h of holidays) holidayMap.set(h.date, h);
  }
  const allHolidays: HolidayEntry[] = Array.from(holidayMap.values()).sort(
    (a, b) => a.date.localeCompare(b.date),
  );

  return { perWeekData, loading, allHolidays };
}
